// src/components/shared/PinterestGrid.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { doc, updateDoc, setDoc, increment, getDoc } from 'firebase/firestore';
import { 
  ArrowBigUp, MessageSquare, Flag, Share2, MapPin, 
  Flame, Star, Pin
} from 'lucide-react';
import { getRelativeTime } from '../../utils/helpers';

export const PinterestGrid = ({ sightings, showToast, setReportId, setZoomImg }) => {
  const navigate = useNavigate();

  const handleUpvote = async (e, s) => {
    e.stopPropagation();
    if (!auth.currentUser) return showToast("Musisz być zalogowany", "error");
    const vId = `v_${auth.currentUser.uid}_${s.id}`;
    if ((await getDoc(doc(db, "votes", vId))).exists()) return showToast("Już głosowałeś", "error");
    await updateDoc(doc(db, "sightings", s.id), { upvotes: increment(1), status: 'verified' });
    await setDoc(doc(db, "users", s.userId), { points: increment(5) }, { merge: true });
    await setDoc(doc(db, "votes", vId), { u: auth.currentUser.uid });
    showToast("Głos zaliczony");
  };

  const handleShare = (e, s) => {
    e.stopPropagation();
    // Generate correct URL based on flair
    const url = s.flair === 'poradnik' && s.seoSlug
      ? `${window.location.origin}/poradnik/${s.seoSlug}`
      : s.plateSlug
        ? `${window.location.origin}/pojazd/${s.plateSlug}`
        : `${window.location.origin}/pojazd/${s.id}`;
    
    navigator.clipboard.writeText(url);
    showToast("Link skopiowany");
  };

  // Helper: Get correct URL for post
  const getPostUrl = (s) => {
    if (s.flair === 'poradnik' && s.seoSlug) {
      return `/poradnik/${s.seoSlug}`;
    }
    if (s.plateSlug) {
      return `/pojazd/${s.plateSlug}`;
    }
    return `/pojazd/${s.id}`;
  };

  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
      {sightings.map(s => (
        <div 
          key={s.id} 
          className={`break-inside-avoid bg-white rounded-xl border-2 overflow-hidden hover:shadow-xl transition-all cursor-pointer group ${
            s.isPinned ? 'border-amber-400 bg-amber-50' :
            s.isAlert ? 'border-red-500' : 
            'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => navigate(getPostUrl(s))}
        >
          <div className="relative aspect-auto">
            <img 
              src={s.imageUrl} 
              className="w-full object-cover" 
              alt="car"
              onClick={(e) => {
                e.stopPropagation();
                setZoomImg(s.imageUrl);
              }}
            />
            {s.isPinned && (
              <div className="absolute top-2 left-2 bg-amber-500 text-white px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 shadow-lg">
                <Pin size={12} fill="white"/>
                PRZYPIĘTE
              </div>
            )}
            {s.isAlert && (
              <div className={`absolute ${s.isPinned ? 'top-12' : 'top-2'} left-2 bg-red-600 text-white px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 shadow-lg animate-pulse`}>
                <Flame size={12} fill="white"/>
                ALERT
              </div>
            )}
            {s.isExclusive && (
              <div className="absolute top-2 right-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 shadow-lg">
                <Star size={12} fill="white"/>
                EXCLUSIVE
              </div>
            )}
            {s.flair === 'poradnik' && (
              <div className="absolute bottom-2 right-2 bg-blue-600 text-white px-2 py-1 rounded-lg text-[10px] font-bold shadow-lg">
                📰 PORADNIK
              </div>
            )}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
              {s.flair === 'poradnik' ? (
                // Poradnik - show title
                <div className="text-white font-bold text-sm line-clamp-2">
                  {s.seoTitle || s.brand}
                </div>
              ) : (
                // Normal post - show plate + brand
                <>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="bg-white text-black px-2 py-1 rounded font-mono font-bold text-sm">
                      {s.plate}
                    </div>
                    <span className="text-xl">{s.countryEmoji || "🇵🇱"}</span>
                  </div>
                  {s.brand && (
                    <p className="text-white font-bold text-sm truncate">{s.brand}</p>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className={`px-2 py-1 rounded text-[10px] font-bold ${
                s.status === 'verified' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-blue-100 text-blue-700'
              }`}>
                {s.status === 'verified' ? 'Zweryfikowano' : 'Do weryfikacji'}
              </span>
            </div>
            
            {/* Description - strip HTML tags for poradniki */}
            <p className="text-sm text-gray-700 mb-3 line-clamp-2">
              {s.flair === 'poradnik' 
                ? s.description.replace(/<[^>]*>/g, '').substring(0, 150) 
                : s.description}
            </p>
            
            <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
              <div className="flex items-center gap-1">
                <MapPin size={12}/>
                <span className="truncate">{s.locationName || "Polska"}</span>
              </div>
              <span>{getRelativeTime(s.createdAt)}</span>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <button 
                  onClick={(e) => handleUpvote(e, s)} 
                  className="flex items-center gap-1 text-gray-500 hover:text-blue-600 transition-colors"
                >
                  <ArrowBigUp size={18} fill={s.upvotes > 0 ? "currentColor" : "none"} />
                  <span className="text-sm font-semibold">{s.upvotes || 0}</span>
                </button>
                <div className="flex items-center gap-1 text-gray-500">
                  <MessageSquare size={16}/>
                  <span className="text-sm font-semibold">{s.commentCount || 0}</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button 
                  onClick={(e) => { e.stopPropagation(); setReportId(s.id); }} 
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Flag size={14}/>
                </button>
                <button 
                  onClick={(e) => handleShare(e, s)} 
                  className="p-1.5 text-gray-400 hover:text-blue-500 transition-colors"
                >
                  <Share2 size={14}/>
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};